import { ArrowRight, Package, Palette, Settings2, Terminal } from "lucide-react";
import GithubMark from "../components/GithubMark";
import PageSEO from "../components/PageSEO";
import SiteFooter from "../components/SiteFooter";
import Breadcrumbs from "../components/Breadcrumbs";
import SiteHeader from "../components/SiteHeader";
import TableOfContents, { type TocItem } from "../components/TableOfContents";
import { useHashScroll } from "../lib/useHashScroll";
import {
  ADAPTER_SNIPPET,
  CHAT_CONFIG_OPTIONS,
  GITHUB_URL,
  INSTALL_SNIPPET,
  NPM_NAME,
  PROVIDER_SNIPPET,
} from "../content/chatSdk";

const TOC: TocItem[] = [
  { id: "overview", label: "Overview" },
  { id: "install", label: "Install" },
  { id: "quickstart", label: "Quickstart" },
  { id: "config", label: "Configuration" },
  { id: "theming", label: "Theming" },
  { id: "used", label: "Where it's used" },
];

const HIGHLIGHTS = [
  {
    icon: <Package className="h-5 w-5 text-primary" />,
    title: "One small dependency",
    body: "A headless hook plus prebuilt widget and full-page chat UI. Streams Markdown, reasoning steps, tool calls, and artifacts out of the box.",
  },
  {
    icon: <Settings2 className="h-5 w-5 text-primary" />,
    title: "Adapter, not lock-in",
    body: "You implement one `ChatAdapter.sendMessage` that returns a stream. Auth, headers, and proxying stay in your control — secrets never ship to the browser.",
  },
  {
    icon: <Palette className="h-5 w-5 text-primary" />,
    title: "Themeable by CSS variables",
    body: "Drives off `--chat-*` custom properties and a no-reset stylesheet, so it inherits your brand without fighting your global CSS.",
  },
];

function CodeBlock({ code }: { code: string }) {
  return (
    <pre className="overflow-x-auto rounded-lg border border-border bg-bg p-4 text-xs leading-relaxed text-text-muted">
      <code>{code}</code>
    </pre>
  );
}

export default function ChatSdkPage() {
  useHashScroll();
  return (
    <>
      <PageSEO
        title="Embeddable React AI Chat Widget — Anter Chat SDK"
        description="Drop a streaming AI chat widget into any React app. Open-source MIT, backend-agnostic via a single adapter, and themeable via CSS variables."
        canonical="/chat-sdk"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "SoftwareSourceCode",
          name: "@anter/ai-chat-sdk",
          description:
            "Embeddable React chat SDK for streaming AI agent conversations. Backend-agnostic adapter, themeable via CSS variables.",
          codeRepository: GITHUB_URL,
          programmingLanguage: "TypeScript",
          license: "https://opensource.org/licenses/MIT",
        }}
      />

      <div className="relative min-h-dvh overflow-x-hidden bg-bg font-sans text-text">
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0"
          style={{
            backgroundImage:
              "radial-gradient(circle at 20% 15%, rgba(16,185,129,0.10), transparent 33%), radial-gradient(circle at 80% 30%, rgba(59,130,246,0.08), transparent 38%)",
          }}
        />

        <SiteHeader scrolled />

        <main
          id="main-content"
          tabIndex={-1}
          className="mx-auto w-full max-w-7xl px-4 pb-24 pt-28 sm:px-5 md:px-8 lg:pt-32"
        >
          <Breadcrumbs items={[{ label: "Chat SDK" }]} />
          {/* Hero */}
          <section id="overview" className="scroll-mt-28">
            <p className="mb-3 text-xs font-semibold uppercase tracking-[0.16em] text-primary-text">
              Open source · React · MIT
            </p>
            {/* The package name is the brand, not the query — people search
                "react ai chat widget", never "@anter/ai-chat-sdk". Lead the H1
                with the thing being searched for and keep the npm name as the
                supporting identifier directly beneath it. */}
            <h1 className="mb-2 text-4xl font-extrabold tracking-tight text-text sm:text-5xl">
              Embeddable React AI chat widget
            </h1>
            <p className="mb-4 font-mono text-sm text-text-muted">{NPM_NAME}</p>
            <p className="mb-6 max-w-2xl text-lg leading-relaxed text-text-muted">
              A drop-in React chat widget for any website. Stream conversations from any Anter agent
              — reasoning steps, tool calls, and rich artifacts included — and theme it to your
              brand with CSS variables. You wire your backend through one typed adapter; nothing
              else is hardcoded.
            </p>

            <div className="mb-4 flex flex-wrap gap-3">
              <a
                href={GITHUB_URL}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-2 rounded-full border border-primary bg-primary px-6 py-3 text-sm font-bold text-primary-text-strong transition hover:opacity-90"
              >
                <GithubMark className="h-4 w-4" />
                View on GitHub
              </a>
              <a
                href="#install"
                className="inline-flex items-center gap-2 rounded-full border border-border bg-bg-secondary px-6 py-3 text-sm font-semibold text-text transition hover:border-primary-border hover:bg-bg-hover"
              >
                Quickstart
                <ArrowRight className="h-4 w-4 text-primary" />
              </a>
              <a
                href="/docs/chat"
                className="inline-flex items-center gap-2 rounded-full border border-border bg-bg-secondary px-6 py-3 text-sm font-semibold text-text transition hover:border-primary-border hover:bg-bg-hover"
              >
                Read Chat Docs
                <ArrowRight className="h-4 w-4 text-primary" />
              </a>
              <a
                href="/resources/ask-anter"
                className="inline-flex items-center gap-2 rounded-full border border-border bg-bg-secondary px-6 py-3 text-sm font-semibold text-text transition hover:border-primary-border hover:bg-bg-hover"
              >
                See it in a real use case
                <ArrowRight className="h-4 w-4 text-primary" />
              </a>
            </div>

            <h2 className="sr-only">Key Features</h2>
            <div className="mt-8 grid gap-4 sm:grid-cols-3">
              {HIGHLIGHTS.map((item) => (
                <div
                  key={item.title}
                  className="rounded-2xl border border-border bg-bg-secondary/60 p-5"
                >
                  <div className="mb-3 flex h-10 w-10 items-center justify-center rounded-xl border border-primary-border bg-primary-dim">
                    {item.icon}
                  </div>
                  <h3 className="mb-1 text-sm font-bold text-text">{item.title}</h3>
                  <p className="text-sm leading-relaxed text-text-muted">{item.body}</p>
                </div>
              ))}
            </div>
          </section>

          {/* TOC + content */}
          <div className="mt-14 grid gap-10 lg:grid-cols-[200px_minmax(0,1fr)]">
            <aside className="hidden lg:block">
              <div className="sticky top-24">
                <TableOfContents items={TOC} />
              </div>
            </aside>

            <div className="min-w-0 space-y-12">
              <section id="install" className="scroll-mt-28">
                <div className="mb-4 flex items-center gap-3">
                  <Terminal className="h-5 w-5 text-primary" />
                  <h2 className="text-2xl font-bold tracking-tight text-text">Install</h2>
                </div>
                <p className="mb-4 max-w-2xl text-sm leading-relaxed text-text-muted">
                  Not on npm yet — install straight from the public repo. It builds itself on
                  install, so no extra step is needed. Import the stylesheet once at your app entry;
                  use <code>styles-no-base.css</code> to avoid global CSS resets and keep full
                  control of your own cascade.
                </p>
                <CodeBlock code={INSTALL_SNIPPET} />
              </section>

              <section id="quickstart" className="scroll-mt-28">
                <div className="mb-6 rounded-2xl border border-primary-border/40 bg-bg-secondary/40 p-6 backdrop-blur-sm">
                  <h2 className="text-xl font-extrabold text-text mb-3">
                    React AI Chat SDK Tutorial: How to Embed a Streaming Chat Widget
                  </h2>
                  <p className="text-sm leading-relaxed text-text-muted mb-4">
                    To embed a streaming AI chat widget into any React application, install{" "}
                    <code>{NPM_NAME}</code>, import its CSS variable stylesheet once, and connect
                    your backend using a single typed <code>ChatAdapter</code> implementation.
                  </p>
                  <ul className="space-y-2.5 text-xs text-text-muted">
                    <li className="flex items-start gap-2">
                      <span className="text-primary font-bold">•</span>
                      <span>
                        <strong>Backend-Agnostic Adapter:</strong> Your server manages
                        authentication and API keys while streaming SSE chunks to the UI via{" "}
                        <code>ChatAdapter.sendMessage</code>.
                      </span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-primary font-bold">•</span>
                      <span>
                        <strong>Theme Custom Properties:</strong> Customize colors and typography
                        using <code>--chat-*</code> CSS variables without breaking your existing app
                        styles.
                      </span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-primary font-bold">•</span>
                      <span>
                        <strong>Built-In Artifact &amp; Reasoning Tracing:</strong> Automatically
                        renders Markdown streams, tool call execution accordions, and sub-agent
                        handoffs.
                      </span>
                    </li>
                  </ul>
                </div>

                <h2 className="mb-3 text-2xl font-bold tracking-tight text-text">
                  Quickstart Code
                </h2>
                <p className="mb-4 max-w-2xl text-sm leading-relaxed text-text-muted">
                  Implement a <code>ChatAdapter</code> that streams from your agent endpoint, then
                  wrap your app in <code>ChatProvider</code> and drop in a <code>ChatWidget</code>.
                </p>
                <h3 className="mb-2 text-sm font-semibold text-text">1 · Adapter</h3>
                <CodeBlock code={ADAPTER_SNIPPET} />
                <h3 className="mb-2 mt-5 text-sm font-semibold text-text">2 · Provider + widget</h3>
                <CodeBlock code={PROVIDER_SNIPPET} />
                <div className="mt-4 rounded-xl border border-primary-border bg-primary-dim/40 px-4 py-3 text-sm text-primary-text">
                  <strong>Keep credentials server-side.</strong> Point the adapter at a proxy path
                  (e.g. <code>/api/...</code>) and inject <code>x-project-id</code> /{" "}
                  <code>x-api-key</code> there — never in browser code.
                </div>
              </section>

              <section id="config" className="scroll-mt-28">
                <h2 className="mb-3 text-2xl font-bold tracking-tight text-text">Configuration</h2>
                <p className="mb-4 max-w-2xl text-sm leading-relaxed text-text-muted">
                  Pass a <code>config</code> object to <code>ChatProvider</code> to tune the
                  experience. The "Landing value" column shows the choices used on this very site.
                </p>
                <div className="overflow-x-auto rounded-2xl border border-border bg-bg-secondary/70 p-5 md:p-6">
                  <table className="min-w-full border-collapse text-left text-sm">
                    <thead>
                      <tr className="border-b border-border text-text">
                        <th className="py-2 pr-4 font-semibold">Option</th>
                        <th className="py-2 pr-4 font-semibold">Landing value</th>
                        <th className="py-2 pr-4 font-semibold">Why used</th>
                        <th className="py-2 font-semibold">Other options</th>
                      </tr>
                    </thead>
                    <tbody className="text-text-muted">
                      {CHAT_CONFIG_OPTIONS.map((option) => (
                        <tr key={option.option} className="border-b border-border/60 align-top">
                          <td className="py-2 pr-4">
                            <code>{option.option}</code>
                          </td>
                          <td className="py-2 pr-4">
                            <code>{option.landingValue}</code>
                          </td>
                          <td className="py-2 pr-4">{option.why}</td>
                          <td className="py-2">{option.alternatives}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </section>

              <section id="theming" className="scroll-mt-28">
                <h2 className="mb-3 text-2xl font-bold tracking-tight text-text">Theming</h2>
                <p className="max-w-2xl text-sm leading-relaxed text-text-muted">
                  The widget renders against <code>--chat-*</code> CSS custom properties (accent,
                  surfaces, borders, message text). Override them in your own stylesheet to match
                  your design system — light and dark adapt automatically. Because the SDK ships its
                  styling as raw, prefixed <code>ais-*</code> classes rather than relying on a
                  Tailwind runtime, it drops cleanly into any host without a build-time theme step.
                </p>
              </section>

              <section id="used" className="scroll-mt-28">
                <h2 className="mb-3 text-2xl font-bold tracking-tight text-text">
                  Guides & Where it's used
                </h2>
                <div className="grid gap-4 md:grid-cols-2">
                  <a
                    href="/resources/ask-anter"
                    className="group block rounded-2xl border border-border bg-bg-secondary/70 p-5 transition hover:border-primary-border hover:bg-bg-hover md:p-6"
                  >
                    <p className="mb-1 text-xs font-semibold uppercase tracking-[0.14em] text-primary-text">
                      Use case
                    </p>
                    <h3 className="mb-1 text-lg font-bold text-text">Build Ask Anter</h3>
                    <p className="mb-3 text-sm leading-relaxed text-text-muted">
                      The exact end-to-end flow we use for the Ask Anter widget on this site: set up
                      the agent stack, then wire it into a production chat widget with{" "}
                      <code>{NPM_NAME}</code>.
                    </p>
                    <span className="inline-flex items-center gap-2 text-sm font-semibold text-primary-text">
                      Read the guide
                      <ArrowRight className="h-4 w-4 transition group-hover:translate-x-0.5" />
                    </span>
                  </a>
                  <a
                    href="https://docs.google.com/document/d/e/2PACX-1vT9v_W7YJ_Xt2d4Ia7Qk1Do18xZYd7Mp9aCMffiFEcTlZAlPAuOvtcS2gk4ur8dUMfYuDsHZeSAREEN/pub"
                    target="_blank"
                    rel="noreferrer"
                    className="group block rounded-2xl border border-border bg-bg-secondary/70 p-5 transition hover:border-primary-border hover:bg-bg-hover md:p-6"
                  >
                    <p className="mb-1 text-xs font-semibold uppercase tracking-[0.14em] text-primary-text">
                      Developer Guide
                    </p>
                    <h3 className="mb-1 text-lg font-bold text-text">
                      React AI Chatbot Widget Guide
                    </h3>
                    <p className="mb-3 text-sm leading-relaxed text-text-muted">
                      Step-by-step external guide covering `@anter/ai-chat-sdk` installation, custom
                      `ChatAdapter` implementation, and streaming widget setup.
                    </p>
                    <span className="inline-flex items-center gap-2 text-sm font-semibold text-primary-text">
                      Read Google Doc Guide
                      <ArrowRight className="h-4 w-4 transition group-hover:translate-x-0.5" />
                    </span>
                  </a>
                </div>
              </section>
            </div>
          </div>
        </main>

        <SiteFooter />
      </div>
    </>
  );
}
